<template>
  <button v-if="iconOnly" class="iconBtn" type="button" title="Add contact" @click="open">
    <AppIcon name="add_contact" :size="16" />
  </button>
  <button v-else class="btn secondary" type="button" @click="open">
    <span>Add contact</span>
  </button>

  <Teleport to="body">
    <Modal v-if="isOpen" title="Add contact" @close="close">
      <div class="addCandidateBody">
        <div class="addCandidateRow">
          <label class="addCandidateField">
            <span>First name</span>
            <input type="text" v-model="form.first_name" placeholder="Jane" />
          </label>
          <label class="addCandidateField">
            <span>Last name</span>
            <input type="text" v-model="form.last_name" placeholder="Doe" />
          </label>
        </div>
        <label class="addCandidateField">
          <span>Email</span>
          <input type="email" v-model="form.email" placeholder="jane@acme.com" @keydown.enter="submit" />
        </label>
        <label class="addCandidateField">
          <span>LinkedIn URL <span class="addCandidateOptional">(optional)</span></span>
          <input type="text" v-model="form.linkedin_url" placeholder="https://www.linkedin.com/in/jane-doe" />
        </label>
        <div class="addCandidateRow">
          <label class="addCandidateField">
            <span>Company <span class="addCandidateOptional">(optional)</span></span>
            <input type="text" v-model="form.account_name" placeholder="Acme Inc." />
          </label>
          <label class="addCandidateField">
            <span>Company domain <span class="addCandidateOptional">(optional)</span></span>
            <input type="text" v-model="form.account_domain" placeholder="acme.com" />
          </label>
        </div>
        <p v-if="error" class="addCandidateError">{{ error }}</p>
      </div>
      <template #footer>
        <button class="btn secondary" @click="close">Cancel</button>
        <button class="btn primary" :disabled="busy || !valid" @click="submit">
          {{ busy ? 'Adding…' : 'Add' }}
        </button>
      </template>
    </Modal>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Modal from './Modal.vue'
import AppIcon from '../../components/AppIcon.vue'
import { apiFetch } from '../../services/api'

const props = defineProps<{
  playId: string
  refreshPlay: () => Promise<void>
  iconOnly?: boolean
}>()

const isOpen = ref(false)
const busy = ref(false)
const error = ref('')
const form = ref({
  first_name: '', last_name: '', email: '', linkedin_url: '', account_name: '', account_domain: '',
})

const valid = computed(() => {
  const e = form.value.email.trim()
  return e.length > 0 && e.includes('@')
})

function open() {
  isOpen.value = true
  error.value = ''
  form.value = { first_name: '', last_name: '', email: '', linkedin_url: '', account_name: '', account_domain: '' }
}

function close() {
  isOpen.value = false
  error.value = ''
}

async function submit() {
  if (!valid.value || busy.value) return
  busy.value = true
  error.value = ''
  const body: Record<string, string> = { kind: 'person', email: form.value.email.trim() }
  if (form.value.first_name.trim()) body.first_name = form.value.first_name.trim()
  if (form.value.last_name.trim()) body.last_name = form.value.last_name.trim()
  if (form.value.linkedin_url.trim()) body.linkedin_url = form.value.linkedin_url.trim()
  if (form.value.account_name.trim()) body.account_name = form.value.account_name.trim()
  if (form.value.account_domain.trim()) body.account_domain = form.value.account_domain.trim()
  try {
    await apiFetch(`/sales-plays/${props.playId}/add-candidate`, { method: 'POST', body })
    close()
    await props.refreshPlay()
  } catch (e: any) {
    error.value = e?.message || 'Failed to add contact'
  } finally {
    busy.value = false
  }
}
</script>

<style scoped>
.addCandidateBody { display: flex; flex-direction: column; gap: var(--space-3); font-family: var(--font-sans); font-size: var(--fs-sm); }
.addCandidateField { display: flex; flex-direction: column; gap: var(--space-1); }
.addCandidateField > span { color: var(--color-text-secondary); font-size: var(--fs-xs); font-weight: var(--fw-semibold); text-transform: uppercase; letter-spacing: var(--tracking-wide); }
.addCandidateField input { padding: var(--space-2); border: 1px solid var(--color-border); border-radius: var(--radius-sm); font-family: var(--font-sans); font-size: var(--fs-sm); }
.addCandidateRow { display: flex; gap: var(--space-3); }
.addCandidateRow .addCandidateField { flex: 1; }
.addCandidateOptional { font-weight: var(--fw-regular); color: var(--color-text-tertiary); text-transform: none; letter-spacing: normal; }
.addCandidateError { color: var(--color-danger, #b91c1c); font-size: var(--fs-sm); margin: 0; }

.iconBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  background: none;
  color: var(--color-text-secondary);
  cursor: pointer;
}
.iconBtn:hover { color: var(--color-text-primary); background: var(--color-bg-subtle); }
</style>
